<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import AgentSection from './AgentSection.vue';
import AgentField from './AgentField.vue';
import AgentToggle from './AgentToggle.vue';
import { createSectionFormProxy } from './createSectionFormProxy';

const props = defineProps({
  form: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
  canManage: { type: Boolean, default: false },
  hasAdvancedManagement: { type: Boolean, default: false },
  brandVoice: { type: Object, default: () => ({}) },
  previewResponse: { type: String, default: '' },
  isSaving: { type: Boolean, default: false },
  isSavingBehavior: { type: Boolean, default: false },
});

const emit = defineEmits(['updateField', 'save', 'saveBehavior', 'openRoute']);
const { t: ce } = useI18n();
const showAdvancedSettings = ref(false);

const brandVoicePreview = computed(() =>
  [props.brandVoice.preview?.persona, props.brandVoice.preview?.tone]
    .filter(Boolean)
    .join(' · ')
);

const brandVoiceApplicationLabel = computed(() => {
  if (
    props.brandVoice.has_unpublished_changes ||
    props.brandVoice.status === 'draft_changed'
  ) {
    return ce('CAPTAIN_EXPERIENCE.WORKSPACE.BRAND_VOICE_PENDING_PUBLICATION');
  }
  if (props.brandVoice.applied) {
    return ce('CAPTAIN_EXPERIENCE.WORKSPACE.BRAND_VOICE_APPLIED');
  }
  return ce('CAPTAIN_EXPERIENCE.WORKSPACE.BRAND_VOICE_NOT_APPLIED');
});

const formModel = createSectionFormProxy(
  () => props.form,
  payload => emit('updateField', payload)
);

const responseLengthShort = 'short';
const responseLengthOptions = [
  {
    value: responseLengthShort,
    label: ce('CAPTAIN_EXPERIENCE.SCRIPT.RESPONSE_LENGTH_SHORT'),
  },
  {
    value: 'medium',
    label: ce('CAPTAIN_EXPERIENCE.SCRIPT.RESPONSE_LENGTH_MEDIUM'),
  },
  {
    value: 'complete',
    label: ce('CAPTAIN_EXPERIENCE.SCRIPT.RESPONSE_LENGTH_COMPLETE'),
  },
];
const languageOptions = [
  {
    value: 'pt-BR',
    label: ce('CAPTAIN_EXPERIENCE.SCRIPT.LANGUAGE_PORTUGUESE'),
  },
  { value: 'en', label: ce('CAPTAIN_EXPERIENCE.SCRIPT.LANGUAGE_ENGLISH') },
  { value: 'es', label: ce('CAPTAIN_EXPERIENCE.SCRIPT.LANGUAGE_SPANISH') },
];
const personaPresets = [
  {
    label: ce('CAPTAIN_EXPERIENCE.SCRIPT.PERSONA_CONSULTATIVE_LABEL'),
    description: ce(
      'CAPTAIN_EXPERIENCE.SCRIPT.PERSONA_CONSULTATIVE_DESCRIPTION'
    ),
    persona: ce('CAPTAIN_EXPERIENCE.SCRIPT.PERSONA_CONSULTATIVE_VALUE'),
    responseLength: responseLengthShort,
  },
  {
    label: ce('CAPTAIN_EXPERIENCE.SCRIPT.PERSONA_WELCOMING_LABEL'),
    description: ce('CAPTAIN_EXPERIENCE.SCRIPT.PERSONA_WELCOMING_DESCRIPTION'),
    persona: ce('CAPTAIN_EXPERIENCE.SCRIPT.PERSONA_WELCOMING_VALUE'),
    responseLength: responseLengthOptions[1].value,
  },
  {
    label: ce('CAPTAIN_EXPERIENCE.SCRIPT.PERSONA_EXPERT_LABEL'),
    description: ce('CAPTAIN_EXPERIENCE.SCRIPT.PERSONA_EXPERT_DESCRIPTION'),
    persona: ce('CAPTAIN_EXPERIENCE.SCRIPT.PERSONA_EXPERT_VALUE'),
    responseLength: responseLengthOptions[2].value,
  },
];

const applyPreset = preset => {
  emit('updateField', { field: 'persona', value: preset.persona });
  emit('updateField', {
    field: 'responseLength',
    value: preset.responseLength,
  });
};

const ruleCount = value =>
  String(value || '')
    .split(/\r?\n/)
    .filter(rule => rule.trim()).length;
</script>

<template>
  <section id="agent-identity" class="flex flex-col gap-8" tabindex="-1">
    <AgentSection :title="ce('CAPTAIN_EXPERIENCE.AUTO.IDENTIDADE_DO_AGENTE')">
      <form class="grid gap-4" @submit.prevent="emit('save')">
        <fieldset
          :disabled="!canManage"
          class="contents disabled:pointer-events-none disabled:opacity-70"
        >
          <div class="flex flex-col gap-4">
            <AgentField
              :label="ce('CAPTAIN_EXPERIENCE.AUTO.NOME_DO_AGENTE')"
              control-id="agent-name"
              :error="errors.name"
              required
            >
              <input
                id="agent-name"
                v-model.trim="formModel.name"
                class="agent-input"
                type="text"
                autocomplete="off"
                required
                :aria-invalid="Boolean(errors.name)"
                :aria-describedby="
                  errors.name ? 'agent-name-description' : undefined
                "
              />
            </AgentField>
            <AgentField
              :label="ce('CAPTAIN_EXPERIENCE.AUTO.AREA_DE_ATENDIMENTO')"
              control-id="agent-product-name"
              :error="errors.productName"
              required
            >
              <input
                id="agent-product-name"
                v-model.trim="formModel.productName"
                class="agent-input"
                type="text"
                autocomplete="off"
                required
                :aria-invalid="Boolean(errors.productName)"
                :aria-describedby="
                  errors.productName
                    ? 'agent-product-name-description'
                    : undefined
                "
              />
            </AgentField>
            <AgentField
              :label="ce('CAPTAIN_EXPERIENCE.AUTO.IDIOMA')"
              control-id="agent-language"
            >
              <select
                id="agent-language"
                v-model="formModel.language"
                class="agent-input"
              >
                <option
                  v-for="language in languageOptions"
                  :key="language.value"
                  :value="language.value"
                >
                  {{ language.label }}
                </option>
              </select>
            </AgentField>
            <AgentField
              :label="ce('CAPTAIN_EXPERIENCE.AUTO.TAMANHO_DAS_RESPOSTAS')"
              control-id="agent-response-length"
            >
              <select
                id="agent-response-length"
                v-model="formModel.responseLength"
                class="agent-input"
              >
                <option
                  v-for="option in responseLengthOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </AgentField>
          </div>

          <AgentField
            :label="ce('CAPTAIN_EXPERIENCE.AUTO.PERSONALIDADE')"
            control-id="agent-persona"
            :helper="
              ce(
                'CAPTAIN_EXPERIENCE.AUTO.DESCREVA_COMO_O_AGENTE_DEVE_SOAR_PARA_O_CLIENTE'
              )
            "
          >
            <textarea
              id="agent-persona"
              v-model="formModel.persona"
              class="agent-textarea"
              rows="3"
              aria-describedby="agent-persona-description"
            />
          </AgentField>
          <div class="flex flex-col gap-2">
            <button
              v-for="preset in personaPresets"
              :key="preset.label"
              type="button"
              class="min-h-11 rounded-lg px-3 py-2 text-left transition hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
              :aria-pressed="
                form.persona === preset.persona &&
                form.responseLength === preset.responseLength
              "
              @click="applyPreset(preset)"
            >
              <span class="text-sm font-semibold text-n-slate-12">
                {{ preset.label }}
              </span>
              <span class="mt-1 block text-xs text-n-slate-11">
                {{ preset.description }}
              </span>
            </button>
          </div>

          <AgentField
            :label="ce('CAPTAIN_EXPERIENCE.AUTO.QUANDO_NAO_SOUBER_RESPONDER')"
            control-id="agent-unknown-response"
          >
            <textarea
              id="agent-unknown-response"
              v-model="formModel.unknownResponse"
              class="agent-textarea"
              rows="3"
            />
          </AgentField>

          <AgentField
            :label="ce('CAPTAIN_EXPERIENCE.AUTO.DESCRICAO_INTERNA')"
            control-id="agent-description"
          >
            <textarea
              id="agent-description"
              v-model="formModel.description"
              class="agent-textarea"
              rows="4"
            />
          </AgentField>

          <div class="flex flex-col gap-3">
            <AgentToggle
              v-model="formModel.featureCitation"
              :label="ce('CAPTAIN_EXPERIENCE.AUTO.MOSTRAR_FONTES')"
              :helper="
                ce('CAPTAIN_EXPERIENCE.AUTO.INDICA_DE_ONDE_VEIO_A_RESPOSTA')
              "
            />
          </div>

          <div
            class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-alpha-2 p-4 sm:p-5"
          >
            <div class="flex min-w-0 items-start gap-3">
              <span
                class="i-lucide-mic-2 mt-0.5 size-5 shrink-0 text-n-blue-11"
                aria-hidden="true"
              />
              <div class="min-w-0">
                <p class="text-sm font-medium text-n-slate-12">
                  {{ ce('CAPTAIN_EXPERIENCE.WORKSPACE.BRAND_VOICE_TITLE') }}
                </p>
                <p class="mt-1 text-xs leading-relaxed text-n-slate-11">
                  {{ ce('CAPTAIN_EXPERIENCE.WORKSPACE.BRAND_VOICE_HELPER') }}
                </p>
                <div class="mt-3 flex flex-wrap gap-2">
                  <span
                    data-testid="brand-voice-configuration-status"
                    class="inline-flex items-center gap-1.5 rounded-full border border-n-weak bg-n-solid-2 px-2.5 py-1 text-xs font-medium text-n-slate-11"
                  >
                    <span
                      class="size-3.5"
                      :class="
                        brandVoice.configured
                          ? 'i-lucide-circle-check text-n-teal-11'
                          : 'i-lucide-circle-dashed text-n-slate-10'
                      "
                      aria-hidden="true"
                    />
                    {{
                      ce(
                        brandVoice.configured
                          ? 'CAPTAIN_EXPERIENCE.WORKSPACE.BRAND_VOICE_CONFIGURED'
                          : 'CAPTAIN_EXPERIENCE.WORKSPACE.BRAND_VOICE_NOT_CONFIGURED'
                      )
                    }}
                  </span>
                  <span
                    data-testid="brand-voice-application-status"
                    class="inline-flex items-center gap-1.5 rounded-full border border-n-weak bg-n-solid-2 px-2.5 py-1 text-xs font-medium text-n-slate-11"
                  >
                    <span
                      class="size-3.5"
                      :class="
                        brandVoice.has_unpublished_changes
                          ? 'i-lucide-clock-3 text-n-amber-11'
                          : brandVoice.applied
                            ? 'i-lucide-badge-check text-n-teal-11'
                            : 'i-lucide-circle-minus text-n-slate-10'
                      "
                      aria-hidden="true"
                    />
                    {{ brandVoiceApplicationLabel }}
                  </span>
                </div>
                <p
                  v-if="brandVoicePreview"
                  data-testid="brand-voice-preview"
                  class="mt-3 line-clamp-2 text-xs leading-relaxed text-n-slate-11"
                >
                  <span class="font-semibold text-n-slate-12">
                    {{ ce('CAPTAIN_EXPERIENCE.WORKSPACE.BRAND_VOICE_PREVIEW') }}
                  </span>
                  {{ brandVoicePreview }}
                </p>
              </div>
            </div>
            <Button
              type="button"
              color="slate"
              variant="faded"
              size="sm"
              class="self-start sm:self-end"
              icon="i-lucide-arrow-up-right"
              :label="
                ce('CAPTAIN_EXPERIENCE.WORKSPACE.OPEN_BRAND_VOICE_EDITOR')
              "
              @click="emit('openRoute', 'brand_voice_editor')"
            />
          </div>

          <button
            v-if="hasAdvancedManagement"
            type="button"
            class="flex min-h-11 items-center gap-2 rounded-lg text-sm font-semibold text-n-blue-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            :aria-expanded="showAdvancedSettings"
            aria-controls="agent-advanced-settings"
            @click="showAdvancedSettings = !showAdvancedSettings"
          >
            <span
              class="size-4"
              :class="
                showAdvancedSettings
                  ? 'i-lucide-chevron-up'
                  : 'i-lucide-chevron-down'
              "
            />
            {{ ce('CAPTAIN_EXPERIENCE.AUTO.AJUSTES_AVANCADOS') }}
          </button>

          <div
            v-else
            class="flex items-start gap-3 rounded-xl border border-n-weak bg-n-alpha-2 p-4"
          >
            <span
              class="i-lucide-lock-keyhole mt-0.5 size-4 shrink-0 text-n-slate-10"
              aria-hidden="true"
            />
            <p class="text-xs leading-relaxed text-n-slate-11">
              {{
                ce('CAPTAIN_EXPERIENCE.WORKSPACE.ADVANCED_IDENTITY_UNAVAILABLE')
              }}
            </p>
          </div>

          <div
            v-if="hasAdvancedManagement && showAdvancedSettings"
            id="agent-advanced-settings"
            class="flex flex-col gap-4 border-t border-n-weak pt-4"
          >
            <AgentField
              :label="ce('CAPTAIN_EXPERIENCE.AUTO.MENSAGEM_AO_FINALIZAR')"
              control-id="agent-resolution-message"
            >
              <textarea
                id="agent-resolution-message"
                v-model="formModel.resolutionMessage"
                class="agent-textarea"
                rows="3"
              />
            </AgentField>
            <div
              class="flex items-start gap-3 rounded-xl border border-n-weak bg-n-alpha-2 p-4"
            >
              <span
                class="i-lucide-cpu mt-0.5 size-4 shrink-0 text-n-blue-11"
                aria-hidden="true"
              />
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  {{
                    ce('CAPTAIN_EXPERIENCE.WORKSPACE.MODEL_INHERITANCE_TITLE')
                  }}
                </p>
                <p class="mt-1 text-xs leading-relaxed text-n-slate-11">
                  {{
                    ce('CAPTAIN_EXPERIENCE.WORKSPACE.MODEL_INHERITANCE_HELPER')
                  }}
                </p>
              </div>
            </div>
            <AgentField
              :label="ce('CAPTAIN_EXPERIENCE.SCRIPT.TEMPERATURE_LABEL')"
              :helper="ce('CAPTAIN_EXPERIENCE.SCRIPT.TEMPERATURE_HELPER')"
              control-id="agent-temperature"
            >
              <div class="flex items-center gap-3">
                <input
                  id="agent-temperature"
                  v-model.number="formModel.temperature"
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  class="w-full"
                  aria-describedby="agent-temperature-description"
                />
                <output
                  for="agent-temperature"
                  class="w-8 text-right text-sm tabular-nums text-n-slate-12"
                >
                  {{ formModel.temperature }}
                </output>
              </div>
            </AgentField>
            <AgentToggle
              v-model="formModel.featureContactAttributes"
              :label="ce('CAPTAIN_EXPERIENCE.SCRIPT.CONTACT_ATTRIBUTES_LABEL')"
              :helper="
                ce('CAPTAIN_EXPERIENCE.SCRIPT.CONTACT_ATTRIBUTES_HELPER')
              "
            />
          </div>

          <Button
            v-if="canManage"
            type="submit"
            icon="i-lucide-save"
            class="w-fit"
            :is-loading="isSaving"
          >
            {{ ce('CAPTAIN_EXPERIENCE.AUTO.SALVAR_IDENTIDADE') }}
          </Button>
        </fieldset>
      </form>
    </AgentSection>

    <AgentSection :title="ce('CAPTAIN_EXPERIENCE.WORKSPACE.RULES_TITLE')">
      <form class="grid gap-5" @submit.prevent="emit('saveBehavior')">
        <fieldset
          :disabled="!canManage"
          class="contents disabled:pointer-events-none disabled:opacity-70"
        >
          <div
            class="flex items-start gap-3 rounded-xl border border-n-weak bg-n-alpha-2 p-4"
          >
            <span
              class="i-lucide-list-tree mt-0.5 size-5 shrink-0 text-n-blue-11"
              aria-hidden="true"
            />
            <p class="text-sm leading-relaxed text-n-slate-11">
              {{ ce('CAPTAIN_EXPERIENCE.WORKSPACE.RULES_DESCRIPTION') }}
            </p>
          </div>

          <div class="grid gap-5 lg:grid-cols-2">
            <AgentField
              :label="ce('CAPTAIN_EXPERIENCE.WORKSPACE.GUIDELINES_TITLE')"
              :helper="ce('CAPTAIN_EXPERIENCE.WORKSPACE.GUIDELINES_HELPER')"
              control-id="agent-response-guidelines"
            >
              <textarea
                id="agent-response-guidelines"
                v-model="formModel.responseGuidelinesText"
                class="agent-textarea min-h-48"
                rows="8"
                :placeholder="
                  ce('CAPTAIN_EXPERIENCE.WORKSPACE.GUIDELINES_PLACEHOLDER')
                "
                aria-describedby="agent-response-guidelines-description"
              />
              <p class="mt-2 text-xs text-n-slate-10">
                {{
                  ce('CAPTAIN_EXPERIENCE.WORKSPACE.RULE_COUNT', {
                    count: ruleCount(form.responseGuidelinesText),
                  })
                }}
              </p>
            </AgentField>

            <AgentField
              :label="ce('CAPTAIN_EXPERIENCE.WORKSPACE.GUARDRAILS_TITLE')"
              :helper="ce('CAPTAIN_EXPERIENCE.WORKSPACE.GUARDRAILS_HELPER')"
              control-id="agent-guardrails"
            >
              <textarea
                id="agent-guardrails"
                v-model="formModel.guardrailsText"
                class="agent-textarea min-h-48"
                rows="8"
                :placeholder="
                  ce('CAPTAIN_EXPERIENCE.WORKSPACE.GUARDRAILS_PLACEHOLDER')
                "
                aria-describedby="agent-guardrails-description"
              />
              <p class="mt-2 text-xs text-n-slate-10">
                {{
                  ce('CAPTAIN_EXPERIENCE.WORKSPACE.RULE_COUNT', {
                    count: ruleCount(form.guardrailsText),
                  })
                }}
              </p>
            </AgentField>
          </div>

          <Button
            v-if="canManage"
            type="submit"
            icon="i-lucide-save"
            class="w-fit"
            :is-loading="isSavingBehavior"
          >
            {{ ce('CAPTAIN_EXPERIENCE.WORKSPACE.SAVE_BEHAVIOR') }}
          </Button>
        </fieldset>
      </form>
    </AgentSection>

    <AgentSection :title="ce('CAPTAIN_EXPERIENCE.AUTO.PREVIA_DA_RESPOSTA')">
      <div class="rounded-xl bg-n-alpha-2 p-4">
        <p class="text-sm leading-relaxed text-n-slate-11">
          {{ previewResponse }}
        </p>
      </div>
      <p class="mt-4 text-xs leading-relaxed text-n-slate-11">
        {{
          ce(
            'CAPTAIN_EXPERIENCE.AUTO.A_PREVIA_AJUDA_A_CONFERIR_TOM_E_CLAREZA_ANTES_DE_TESTAR_'
          )
        }}
      </p>
    </AgentSection>
  </section>
</template>

<style scoped>
.agent-input,
.agent-textarea {
  @apply w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2.5 text-sm leading-normal text-n-slate-12 outline-none transition placeholder:text-n-slate-10 hover:border-n-slate-6 focus:border-n-brand focus:ring-2 focus:ring-n-brand/20;
}

.agent-input {
  min-height: 2.5rem;
}

.agent-textarea {
  resize: vertical;
}
</style>
